import React, { useState } from 'react';

interface WeatherAlert {
  id: string;
  severity: 'warning' | 'watch' | 'advisory';
  condition: string;
  icon: string;
  message: string;
  affectedDate: string;
  affectedCount: number;
  affectedType: string;
  temp?: string;
  wind?: string;
}

// Dummy alerts — replace with real weather API data when WTH module is implemented
const DUMMY_ALERTS: WeatherAlert[] = [
  {
    id: 'w1',
    severity: 'warning',
    condition: 'Heavy Rain & Thunderstorm',
    icon: '⛈️',
    message: 'Heavy rain expected with possible thunderstorms. Outdoor work may be unsafe.',
    affectedDate: 'Tomorrow, Jul 15',
    affectedCount: 3,
    affectedType: 'site visits',
    temp: '68°F',
    wind: '24 mph',
  },
  {
    id: 'w2',
    severity: 'watch',
    condition: 'High Wind Watch',
    icon: '💨',
    message: 'Sustained winds up to 35 mph. Roofing and elevated work not advised.',
    affectedDate: 'Thu, Jul 17',
    affectedCount: 2,
    affectedType: 'inspections',
    temp: '72°F',
    wind: '35 mph gusts',
  },
  {
    id: 'w3',
    severity: 'advisory',
    condition: 'Heat Advisory',
    icon: '🌡️',
    message: 'Heat index near 105°F. Ensure crews take frequent breaks and stay hydrated.',
    affectedDate: 'Fri, Jul 18',
    affectedCount: 4,
    affectedType: 'jobs',
    temp: '98°F',
    wind: '8 mph',
  },
];

const SEVERITY_STYLE = {
  warning:  { bar: 'bg-red-500',    bg: 'bg-red-50 border-red-200',    text: 'text-red-700',    badge: 'bg-red-100 text-red-700 border-red-300',    label: 'Warning' },
  watch:    { bar: 'bg-amber-500',  bg: 'bg-amber-50 border-amber-200', text: 'text-amber-700',  badge: 'bg-amber-100 text-amber-700 border-amber-300', label: 'Watch' },
  advisory: { bar: 'bg-blue-400',   bg: 'bg-blue-50 border-blue-200',   text: 'text-blue-700',   badge: 'bg-blue-100 text-blue-700 border-blue-300',    label: 'Advisory' },
};

const WeatherAlertBanner: React.FC = () => {
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<string | null>(null);

  const visible = DUMMY_ALERTS.filter((a) => !dismissed.has(a.id));
  if (visible.length === 0) return null;

  return (
    <div className="flex-shrink-0 border-b border-[#EDEDED]">
      {/* Header row */}
      <div className="flex items-center gap-2 px-4 py-1.5 bg-ui-subtle">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-amber-500 flex-shrink-0">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
          <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
        </svg>
        <span className="text-[10px] font-bold uppercase tracking-wider text-ui-text2">
          Weather Alerts — {visible.length} active
        </span>
        <span className="text-[10px] text-ui-text3 ml-auto">Dummy data · WTH module</span>
      </div>

      {/* Alert cards */}
      <div className="flex gap-2 px-4 py-2 overflow-x-auto no-scrollbar">
        {visible.map((alert) => {
          const s = SEVERITY_STYLE[alert.severity];
          const isExpanded = expanded === alert.id;

          return (
            <div
              key={alert.id}
              className={`flex-shrink-0 rounded-xl border overflow-hidden transition-all ${s.bg} ${isExpanded ? 'w-72' : 'w-56'}`}
            >
              {/* Severity bar */}
              <div className={`h-1 w-full ${s.bar}`} />

              <div className="p-2.5">
                {/* Top row */}
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-1.5">
                    <span className="text-base leading-none">{alert.icon}</span>
                    <div>
                      <div className="flex items-center gap-1">
                        <span className={`text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded border ${s.badge}`}>
                          {s.label}
                        </span>
                      </div>
                      <p className={`text-[11px] font-semibold mt-0.5 leading-tight ${s.text}`}>
                        {alert.condition}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setDismissed((d) => new Set([...d, alert.id]))}
                    className="text-ui-text3 hover:text-ui-text flex-shrink-0 -mt-0.5"
                  >
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                </div>

                {/* Stats row */}
                <div className="flex items-center gap-2 mt-2">
                  {alert.temp && (
                    <span className="flex items-center gap-0.5 text-[10px] text-ui-text3">
                      🌡 {alert.temp}
                    </span>
                  )}
                  {alert.wind && (
                    <span className="flex items-center gap-0.5 text-[10px] text-ui-text3">
                      💨 {alert.wind}
                    </span>
                  )}
                </div>

                {/* Affected */}
                <div className={`mt-2 text-[10px] font-medium ${s.text}`}>
                  {alert.affectedCount} {alert.affectedType} affected · {alert.affectedDate}
                </div>

                {/* Expanded detail */}
                {isExpanded && (
                  <p className="mt-2 text-[10px] text-ui-text2 leading-relaxed border-t border-current/10 pt-2">
                    {alert.message}
                  </p>
                )}

                {/* Expand toggle */}
                <button
                  type="button"
                  onClick={() => setExpanded(isExpanded ? null : alert.id)}
                  className={`mt-1.5 text-[10px] font-medium ${s.text} hover:underline`}
                >
                  {isExpanded ? 'Less ↑' : 'Details ↓'}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WeatherAlertBanner;
